import {
  signUp,
  signIn,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  updatePassword,
  getCurrentUser,
  fetchAuthSession,
  signInWithRedirect
} from 'aws-amplify/auth';

export interface SignUpParams {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface SignInParams {
  email: string;
  password: string;
}

export interface ConfirmSignUpParams {
  email: string;
  code: string;
}

export interface ResetPasswordParams {
  email: string;
}

export interface ConfirmResetPasswordParams {
  email: string;
  code: string;
  newPassword: string;
}

export const authService = {
  /**
   * Sign up with email and password
   */
  signUp: async ({ email, password, firstName, lastName }: SignUpParams) => {
    try {
      const { isSignUpComplete, userId, nextStep } = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            given_name: firstName,
            family_name: lastName
          },
          autoSignIn: true
        }
      });

      return {
        success: true,
        isSignUpComplete,
        userId,
        nextStep
      };
    } catch (error: any) {
      console.error('Error signing up:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign up'
      };
    }
  },

  /**
   * Confirm sign up with verification code
   */
  confirmSignUp: async ({ email, code }: ConfirmSignUpParams) => {
    try {
      const { isSignUpComplete, nextStep } = await confirmSignUp({
        username: email,
        confirmationCode: code
      });

      return {
        success: true,
        isSignUpComplete,
        nextStep
      };
    } catch (error: any) {
      console.error('Error confirming sign up:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm sign up'
      };
    }
  },

  /**
   * Resend verification code
   */
  resendVerificationCode: async (email: string) => {
    try {
      await resendSignUpCode({ username: email });
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Error resending code:', error);
      return {
        success: false,
        error: error.message || 'Failed to resend verification code'
      };
    }
  },

  /**
   * Sign in with email and password
   */
  signIn: async ({ email, password }: SignInParams) => {
    try {
      const { isSignedIn, nextStep } = await signIn({
        username: email,
        password
      });

      return {
        success: true,
        isSignedIn,
        nextStep
      };
    } catch (error: any) {
      console.error('Error signing in:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign in'
      };
    }
  },

  /**
   * Sign in with Google
   */
  signInWithGoogle: async () => {
    try {
      await signInWithRedirect({ provider: 'Google' });
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  },

  /**
   * Sign out
   */
  signOut: async () => {
    try {
      await signOut();
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Error signing out:', error);
      return {
        success: false,
        error: error.message || 'Failed to sign out'
      };
    }
  },

  /**
   * Get current authenticated user
   */
  getCurrentUser: async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      // Extract custom claims from ID token
      const idToken = session.tokens?.idToken;
      const userStateId = idToken?.payload['custom:UserStateID'] as string;

      // Try custom attributes first, then fall back to standard Cognito attributes
      const customFirstName = idToken?.payload['custom:playerFirstName'] as string;
      const customLastName = idToken?.payload['custom:playerLastName'] as string;
      const standardFirstName = idToken?.payload['given_name'] as string;
      const standardLastName = idToken?.payload['family_name'] as string;

      const firstName = customFirstName || standardFirstName || '';
      const lastName = customLastName || standardLastName || '';

      return {
        success: true,
        user: {
          cognitoSub: user.userId,
          username: user.username,
          email: idToken?.payload.email as string,
          UserStateID: userStateId ? parseInt(userStateId) : null,
          playerFirstName: firstName,
          playerLastName: lastName,
          profileImageUrl: idToken?.payload['custom:profileImageUrl'] as string || null,
          provider: idToken?.payload['identities'] ? 'oauth' : 'email'
        }
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Not authenticated',
        user: null
      };
    }
  },

  /**
   * Get JWT token for API calls
   */
  getJwtToken: async () => {
    try {
      const session = await fetchAuthSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting JWT token:', error);
      return null;
    }
  },

  /**
   * Request password reset
   */
  resetPassword: async ({ email }: ResetPasswordParams) => {
    try {
      const output = await resetPassword({ username: email });
      return {
        success: true,
        nextStep: output.nextStep
      };
    } catch (error: any) {
      console.error('Error resetting password:', error);
      return {
        success: false,
        error: error.message || 'Failed to reset password'
      };
    }
  },

  /**
   * Confirm password reset with code
   */
  confirmResetPassword: async ({ email, code, newPassword }: ConfirmResetPasswordParams) => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword
      });
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Error confirming password reset:', error);
      return {
        success: false,
        error: error.message || 'Failed to confirm password reset'
      };
    }
  },

  /**
   * Update password for currently authenticated user
   */
  updatePassword: async (oldPassword: string, newPassword: string) => {
    try {
      await updatePassword({
        oldPassword,
        newPassword
      });
      return {
        success: true
      };
    } catch (error: any) {
      console.error('Error updating password:', error);
      return {
        success: false,
        error: error.message || 'Failed to update password'
      };
    }
  }
};
